import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Btn } from "../components/ui";
import { authFetch, apiUrl } from "../utils/api";
import { showGlobalToast } from "../utils/globalNotify";

type DocType = { doc_type: string; label: string; uploaded?: boolean; file_name?: string | null; template_ready?: boolean; updated_at?: string | null };
type Placeholder = { key: string; token: string; label: string; group: string };
type SignedDoc = { id: number; doc_type: string; label: string; file_name?: string | null; uploaded_by?: string | null; created_at?: string };

// Authentifizierten Download anstoßen (Cookie/Bearer via authFetch), Dateiname aus
// Content-Disposition oder Fallback.
async function downloadAuthed(path: string, fallbackName: string) {
  const resp = await authFetch(apiUrl(path));
  if (!resp.ok) {
    const msg = await resp.json().catch(() => ({}));
    throw new Error((msg.errors || []).join(" ") || `HTTP ${resp.status}`);
  }
  const cd = resp.headers.get("Content-Disposition") || "";
  const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
  const name = m ? decodeURIComponent(m[1].replace(/"/g, "")) : fallbackName;
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/50 p-4" onClick={onClose}>
      <div
        className={`mt-[6vh] w-full ${wide ? "max-w-3xl" : "max-w-xl"} rounded-[16px] border border-[var(--line)] bg-white shadow-[0_24px_60px_rgba(16,32,38,.28)]`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-[var(--line)] px-5 py-3">
          <div className="text-base font-extrabold tracking-[-.02em] text-[var(--ink)]">{title}</div>
          <Btn variant="secondary" className="py-1 text-xs" onClick={onClose}>Schließen</Btn>
        </div>
        <div className="max-h-[74vh] overflow-auto p-5">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

function UploadButton({ accept, onFile, children, secondary = true }: { accept: string; onFile: (f: File) => void; children: React.ReactNode; secondary?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <Btn variant={secondary ? "secondary" : "primary"} className="py-[7px] text-xs" onClick={() => ref.current?.click()}>{children}</Btn>
      <input
        ref={ref}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }}
      />
    </>
  );
}

// ---- Globale Vorlagen (Vertrag/AVV/TOM) verwalten -------------------------
export function GlobalDocTemplatesModal({ onClose }: { onClose: () => void }) {
  const [types, setTypes] = useState<DocType[]>([]);
  const [placeholders, setPlaceholders] = useState<Placeholder[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await authFetch(apiUrl("/api/v1/admin_document_templates"));
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setTypes(data.templates || []);
      setPlaceholders(data.placeholders || []);
    } catch {
      showGlobalToast("Vorlagen konnten nicht geladen werden.", "error");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const upload = async (docType: string, file: File) => {
    setBusy(docType);
    try {
      const fd = new FormData();
      fd.append("word_file", file);
      const resp = await authFetch(apiUrl(`/api/v1/admin_document_templates/${docType}`), { method: "POST", body: fd });
      if (!resp.ok) { const m = await resp.json().catch(() => ({})); throw new Error((m.errors || []).join(" ") || `HTTP ${resp.status}`); }
      showGlobalToast("Vorlage hochgeladen.", "success");
      await load();
    } catch (e) {
      showGlobalToast("Upload fehlgeschlagen. " + (e instanceof Error ? e.message : ""), "error");
    } finally {
      setBusy(null);
    }
  };

  const groups = Array.from(new Set(placeholders.map((p) => p.group)));

  return (
    <Modal title="Dokument-Vorlagen (Vertrag / AVV / TOM)" onClose={onClose} wide>
      <p className="mb-4 text-sm text-[var(--ink-mid)]">
        Lade je Typ eine Word-Vorlage (.docx) hoch. Beim Generieren pro Unternehmen werden die Platzhalter automatisch mit den Firmendaten und dem Preis gefüllt.
      </p>
      {loading ? (
        <div className="text-sm text-[var(--ink-muted)]">Lädt…</div>
      ) : (
        <div className="grid gap-2">
          {types.map((t) => (
            <div key={t.doc_type} className="flex flex-wrap items-center gap-3 rounded-[10px] border border-[var(--line)] p-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-[var(--ink)]">{t.label}</div>
                <div className="text-xs text-[var(--ink-muted)]">
                  {t.uploaded ? `Hinterlegt: ${t.file_name}` : "Noch keine Vorlage hinterlegt"}
                </div>
              </div>
              {t.uploaded && (
                <Btn variant="secondary" className="py-[7px] text-xs" onClick={() => downloadAuthed(`/api/v1/admin_document_templates/${t.doc_type}/download`, `${t.label}.docx`).catch(() => showGlobalToast("Download fehlgeschlagen.", "error"))}>
                  Herunterladen
                </Btn>
              )}
              <UploadButton accept=".docx" secondary={false} onFile={(f) => upload(t.doc_type, f)}>
                {busy === t.doc_type ? "Lädt…" : t.uploaded ? "Ersetzen" : "Hochladen"}
              </UploadButton>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5">
        <div className="mb-2 text-xs font-bold uppercase tracking-wide text-[var(--ink-muted)]">Verfügbare Platzhalter (in der Word-Datei als {"{{"}token{"}}"})</div>
        <div className="grid gap-3">
          {groups.map((g) => (
            <div key={g}>
              <div className="mb-1 text-[.72rem] font-bold text-[var(--teal-dark)]">{g}</div>
              <div className="flex flex-wrap gap-1.5">
                {placeholders.filter((p) => p.group === g).map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    title={`${p.label} – klicken zum Kopieren`}
                    onClick={() => { navigator.clipboard?.writeText(p.token); showGlobalToast(`${p.token} kopiert`, "success"); }}
                    className="rounded-[6px] border border-[var(--line)] bg-[var(--surface)] px-2 py-1 font-mono text-[.72rem] text-[var(--ink-mid)] hover:border-[var(--teal-mid)] hover:text-[var(--teal-dark)]"
                  >
                    {p.token}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

// ---- Dokumente eines Unternehmens -----------------------------------------
export function BusinessDocumentsModal({ businessId, businessName, businessSlug, onClose }: { businessId: number; businessName: string; businessSlug: string; onClose: () => void }) {
  const [types, setTypes] = useState<DocType[]>([]);
  const [docs, setDocs] = useState<SignedDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await authFetch(apiUrl(`/api/v1/businesses/${businessId}/documents`));
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setTypes(data.types || []);
      setDocs(data.documents || []);
    } catch {
      showGlobalToast("Dokumente konnten nicht geladen werden.", "error");
    } finally {
      setLoading(false);
    }
  }, [businessId]);
  useEffect(() => { void load(); }, [load]);

  const generate = async (t: DocType) => {
    setBusy(`gen-${t.doc_type}`);
    try {
      await downloadAuthed(`/api/v1/businesses/${businessId}/documents/${t.doc_type}/generate`, `${t.label}_${businessSlug}.docx`);
    } catch (e) {
      showGlobalToast("Generieren fehlgeschlagen. " + (e instanceof Error ? e.message : ""), "error");
    } finally {
      setBusy(null);
    }
  };

  const uploadSigned = async (t: DocType, file: File) => {
    setBusy(`up-${t.doc_type}`);
    try {
      const fd = new FormData();
      fd.append("signed_file", file);
      const resp = await authFetch(apiUrl(`/api/v1/businesses/${businessId}/documents/${t.doc_type}`), { method: "POST", body: fd });
      if (!resp.ok) { const m = await resp.json().catch(() => ({})); throw new Error((m.errors || []).join(" ") || `HTTP ${resp.status}`); }
      showGlobalToast("Unterschriebenes Dokument hochgeladen.", "success");
      await load();
    } catch (e) {
      showGlobalToast("Upload fehlgeschlagen. " + (e instanceof Error ? e.message : ""), "error");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (doc: SignedDoc) => {
    if (!window.confirm(`Dokument „${doc.file_name || doc.label}" wirklich löschen?`)) return;
    try {
      const resp = await authFetch(apiUrl(`/api/v1/businesses/${businessId}/documents/${doc.id}`), { method: "DELETE" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      await load();
    } catch {
      showGlobalToast("Löschen fehlgeschlagen.", "error");
    }
  };

  return (
    <Modal title={`Dokumente – ${businessName}`} onClose={onClose} wide>
      {loading ? (
        <div className="text-sm text-[var(--ink-muted)]">Lädt…</div>
      ) : (
        <div className="grid gap-3">
          {types.map((t) => {
            const forType = docs.filter((d) => d.doc_type === t.doc_type);
            return (
              <div key={t.doc_type} className="rounded-[10px] border border-[var(--line)] p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-[var(--ink)]">{t.label}</div>
                    {!t.template_ready && <div className="text-xs text-[var(--orange-dark)]">Keine globale Vorlage hinterlegt – erst unter „Dokument-Vorlagen" hochladen.</div>}
                  </div>
                  <Btn variant="secondary" className="py-[7px] text-xs" disabled={!t.template_ready || busy === `gen-${t.doc_type}`} onClick={() => generate(t)}>
                    {busy === `gen-${t.doc_type}` ? "Erzeuge…" : "Generieren (Word)"}
                  </Btn>
                  <UploadButton accept=".pdf,.docx,.doc,.png,.jpg,.jpeg" onFile={(f) => uploadSigned(t, f)}>
                    {busy === `up-${t.doc_type}` ? "Lädt…" : "Unterschrieben hochladen"}
                  </UploadButton>
                </div>
                {forType.length > 0 && (
                  <div className="mt-2 grid gap-1.5">
                    {forType.map((d) => (
                      <div key={d.id} className="flex flex-wrap items-center gap-2 rounded-[8px] bg-[var(--surface)] px-2.5 py-1.5">
                        <span className="min-w-0 flex-1 truncate text-xs text-[var(--ink-mid)]">
                          {d.file_name}
                          {d.uploaded_by ? ` · ${d.uploaded_by}` : ""}
                          {d.created_at ? ` · ${new Date(d.created_at).toLocaleDateString("de-DE")}` : ""}
                        </span>
                        <button type="button" className="text-xs font-semibold text-[var(--teal-dark)] hover:underline" onClick={() => downloadAuthed(`/api/v1/businesses/${businessId}/documents/${d.id}/download`, d.file_name || "dokument").catch(() => showGlobalToast("Download fehlgeschlagen.", "error"))}>Download</button>
                        <button type="button" className="text-xs font-semibold text-[#B33636] hover:underline" onClick={() => remove(d)}>Löschen</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
